import { forwardRef, type KeyboardEvent } from 'react';
import { Search, X } from '../../../icons';
import { Input, type InputProps } from './Input';

/**
 * SearchInput — 좌 돋보기 · 우 지우기 (02-components.md B14).
 *
 * ★ **지우기는 값이 있을 때만 뜬다** — 늘 떠 있으면 「지울 것이 있다」는 거짓 신호가 된다.
 * ★ `Esc` 로 비운다. 손이 마우스로 가지 않아도 되는 자리다.
 * ★ 디바운스는 **호출부 책임**이다 — 여기서 걸면 화면마다 다른 지연을 못 준다.
 */
export interface SearchInputProps extends Omit<InputProps, 'type' | 'prefix'> {
  /** 지우기·Esc 가 부르는 것. 없으면 지우기 단추를 안 그린다. */
  onClear?: () => void;
  /** 지우기 단추의 읽어 주는 이름. */
  clearLabel?: string;
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(
  { onClear, clearLabel = '검색어 지우기', value, suffix, onKeyDown, ...rest },
  ref,
) {
  const hasValue = value != null && String(value).length > 0;

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(e);
    if (e.key === 'Escape' && hasValue && onClear) {
      e.stopPropagation();   // 검색칸을 비우는 Esc 가 모달까지 닫으면 안 된다
      onClear();
    }
  };

  return (
    <Input
      ref={ref}
      type="search"
      value={value}
      prefix={<Search aria-hidden />}
      suffix={
        hasValue && onClear ? (
          <button type="button" className="wc-input__btn" onClick={onClear} aria-label={clearLabel}>
            <X aria-hidden />
          </button>
        ) : suffix
      }
      onKeyDown={handleKeyDown}
      {...rest}
    />
  );
});
